import React, { FC, ReactNode } from 'react'
import classNames from 'classnames'
import { format } from 'date-fns'
import { motion } from 'framer-motion'
import { CountdownToMidnight } from 'src/views/PreSales/PreSalesGiveawayView/components/Hero/CountdownToMidnight'
import { WeeklyCountdownTimer } from 'src/views/PreSales/PreSalesGiveawayView/components/Hero/WeeklyCountdownTimer'
import { slugs } from '@/constants/slugs'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { SweepstakeObject } from '@/types/codegen-federation'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import {
  GEyebrowLG,
  GEyebrowXL,
  GHeadingLG,
  GHeadingSM,
  GResponsiveText,
  GTitleMD,
  GTitleSM,
  GTitleXL,
} from '@/views/PreSales/PreSalesGiveawayView/components/UI/Typography'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'

interface Props {
  theatricalSlug: string
  winnerAnnouncedDate: SweepstakeObject['winnerAnnouncedDate']
  isActive: boolean
}

const DailyEntriesSlugs = new Set([
  slugs.kingOfKings,
  slugs.youngWashington,
  slugs.sketch,
  slugs.senior,
  slugs.truthAndTreason,
  slugs.david,
  slugs.iWasAStranger,
  slugs.soloMio,
])

export const GiveawayPromoBanner: FC<Props> = ({ theatricalSlug, winnerAnnouncedDate, isActive }) => {
  const { t } = useTranslate('tickets')
  const isDarkText = [slugs.soloMio].includes(theatricalSlug)
  const { normal, refetch, isReady, dailyBonusEntries, minRewardTicketQuantity } = useGiveawaySchedule(theatricalSlug)

  const multiplier = normal?.multiplier ?? 1

  if (!isReady) return null
  if (theatricalSlug === slugs.homesteadSeries) return null

  if (!isActive) {
    return (
      <YellowBannerWrapper theatricalSlug={theatricalSlug}>
        <GResponsiveText
          color={isDarkText ? 'giveaway-text-primary-dark' : 'giveaway-text-primary-light'}
          className="block"
          MobileTextComponent={GEyebrowLG}
          DesktopTextComponent={GEyebrowXL}
        >
          {t('giveawayHasEnded', 'Giveaway Has Ended')}
        </GResponsiveText>
        <GResponsiveText
          color={isDarkText ? 'giveaway-text-primary-dark' : 'giveaway-text-primary-light'}
          MobileTextComponent={GTitleSM}
          DesktopTextComponent={GTitleSM}
        >
          {t('winnerAnnouncedGiveaway', 'Winner Announced {{winnerDate}}', {
            winnerDate: format(winnerAnnouncedDate, 'MMMM d, yyyy'),
          })}
          *
        </GResponsiveText>
      </YellowBannerWrapper>
    )
  }

  return (
    <YellowBannerWrapper theatricalSlug={theatricalSlug}>
      {DailyEntriesSlugs.has(theatricalSlug) && (
        <>
          <GResponsiveText
            color={isDarkText ? 'giveaway-text-primary-dark' : 'giveaway-text-primary-light'}
            className="block"
            MobileTextComponent={GEyebrowLG}
            DesktopTextComponent={GEyebrowXL}
          >
            {t('todayOnly', 'Today Only')}
          </GResponsiveText>

          <GResponsiveText
            color={isDarkText ? 'giveaway-text-primary-dark' : 'giveaway-text-primary-light'}
            className="block"
            MobileTextComponent={GHeadingSM}
            DesktopTextComponent={GHeadingLG}
          >
            {t('getBonusEntriesGiveaway', 'Get {{dailyBonusEntries}} Bonus Entries', { dailyBonusEntries })}
          </GResponsiveText>

          <GBFadeInExperiment experimentName="giveaway-countdown-timer" defaultValue={false}>
            <ExperimentVariation variation={true}>
              <CountdownToMidnight onCountdownEnd={refetch} isDarkText={isDarkText} />
            </ExperimentVariation>
            <ExperimentVariation default>
              <GResponsiveText
                color={isDarkText ? 'giveaway-text-primary-dark' : 'giveaway-text-primary-light'}
                className="block"
                MobileTextComponent={GEyebrowLG}
                DesktopTextComponent={GEyebrowXL}
              >
                {t('minRewardQuantityV2', {
                  count: minRewardTicketQuantity,
                  minRewardTicketQuantity,
                  defaultValue_one: 'When you buy any ticket pack',
                  defaultValue_other: 'When you buy {{minRewardTicketQuantity}}+ tickets',
                })}
              </GResponsiveText>
            </ExperimentVariation>
          </GBFadeInExperiment>
        </>
      )}

      {!DailyEntriesSlugs.has(theatricalSlug) && (
        <>
          <GResponsiveText
            color={isDarkText ? 'giveaway-text-primary-dark' : 'giveaway-text-primary-light'}
            className="block"
            MobileTextComponent={GEyebrowLG}
            DesktopTextComponent={GEyebrowXL}
          >
            {t('thisWeekOnly', 'This week only')}
          </GResponsiveText>
          <GResponsiveText
            color={isDarkText ? 'giveaway-text-primary-dark' : 'giveaway-text-primary-light'}
            className="block !font-medium uppercase"
            MobileTextComponent={GTitleMD}
            DesktopTextComponent={GTitleXL}
          >
            <Translate t={t} i18nKey="getMultiplierYourEntires">
              Get <span className="!font-extrabold">{{ multiplier }}x</span> Your Entries
            </Translate>
          </GResponsiveText>

          <div className="flex justify-center gap-2 md:justify-start">
            <GResponsiveText
              color={isDarkText ? 'giveaway-text-primary-dark' : 'giveaway-text-primary-light'}
              className="!font-medium uppercase"
              MobileTextComponent={GEyebrowLG}
              DesktopTextComponent={GEyebrowXL}
            >
              {t('endingIn', 'Ending In')}
            </GResponsiveText>

            <WeeklyCountdownTimer
              theatricalSlug={theatricalSlug}
              onCountdownEnd={refetch}
              className="!homestead-eyebrow-lg"
            />
          </div>
        </>
      )}
    </YellowBannerWrapper>
  )
}

const containerVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
}

const YellowBannerWrapper = ({ theatricalSlug, children }: { theatricalSlug: string; children: ReactNode }) => {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className={classNames(
        '-mx-6 py-2.5 text-center md:mx-0 md:w-fit md:px-6 md:py-4 md:text-left bg-giveaway-secondary',
        theatricalSlug === slugs.senior && '!bg-giveaway-secondary-light',
        theatricalSlug === slugs.truthAndTreason && '!bg-giveaway-primary-accent',
        theatricalSlug === slugs.david && '!bg-giveaway-primary-accent',
        theatricalSlug === slugs.iWasAStranger && '!bg-giveaway-primary-accent-dark',
        theatricalSlug === slugs.soloMio && '!bg-giveaway-secondary-accent',
      )}
    >
      {children}
    </motion.div>
  )
}
